import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import './JobSummaryTooltip.css';

const moment = require('moment');

const ANCHOR_GAP = 8;
const VIEWPORT_MARGIN = 8;

const joinNonEmpty = (parts, separator) =>
  parts.filter((part) => part && String(part).trim()).join(separator);

const formatDate = (value, format) =>
  value ? moment(value).format(format) : '';

/**
 * Groups what a confirmation caller needs on hand while on the phone with the
 * provider's office, in the order they work through it.
 * @param job - A work order row from /getworkorders
 * @returns Titled sections of labelled values; empty values are left blank
 */
export const buildJobSummarySections = (job) => [
  {
    title: 'Claimant',
    rows: [
      {
        label: 'Name',
        value: joinNonEmpty([job.clmFirstName, job.clmLastName], ' '),
      },
      { label: 'DOB', value: formatDate(job.clmDOB, 'MM/DD/YYYY') },
      { label: 'Phone', value: job.clmPhone },
      { label: 'Notes', value: job.clmNote, highlight: true },
    ],
  },
  {
    title: 'Appointment',
    rows: [
      {
        label: 'Date/Time',
        value: joinNonEmpty(
          [
            formatDate(job.jobDateTime, 'ddd MM/DD/YYYY hh:mm A'),
            job.finishTimeString,
          ],
          ' to ',
        ),
      },
      {
        label: 'Address',
        value: joinNonEmpty(
          [
            job.aptStreet,
            job.aptCity,
            joinNonEmpty([job.aptState, job.aptZip], ' '),
          ],
          ', ',
        ),
      },
      { label: 'Provider', value: job.providerName },
      { label: 'Appt Name/Specialty', value: job.aptName },
      { label: 'Job Type', value: job.jobTypeName },
      { label: 'Appt Details', value: job.aptDirection },
      { label: 'Appt Phone', value: job.aptPhone },
      { label: 'Int Appt Directions', value: job.intApptNotes },
    ],
  },
  {
    title: 'Interpreter',
    rows: [
      {
        label: 'Name',
        value: joinNonEmpty([job.intFirstName, job.intLastName], ' '),
      },
      { label: 'Cell/Text', value: job.intCellTxt },
      { label: 'Phone Only', value: job.intPhoneOnly },
    ],
  },
];

/**
 * Hover card for a work order grid. The grid drives it through show/hide so
 * hovering never re-renders the grid itself.
 */
export class JobSummaryTooltip extends Component {
  constructor(props) {
    super(props);
    this.tooltipRef = React.createRef();
    this.state = { job: null, anchorRect: null, position: null };
  }

  show(job, anchorRect) {
    this.setState({ job, anchorRect, position: null });
  }

  hide() {
    this.setState({ job: null, anchorRect: null, position: null });
  }

  componentDidUpdate() {
    // Measured after an invisible first render so it can be kept inside the viewport.
    if (this.state.job && this.state.position === null && this.tooltipRef.current) {
      this.setState({ position: this.calculatePosition() });
    }
  }

  calculatePosition() {
    const { anchorRect } = this.state;
    const { offsetWidth, offsetHeight } = this.tooltipRef.current;
    const clamp = (value, size, viewportSize) =>
      Math.max(
        VIEWPORT_MARGIN,
        Math.min(value, viewportSize - size - VIEWPORT_MARGIN),
      );

    return {
      left: clamp(anchorRect.right + ANCHOR_GAP, offsetWidth, window.innerWidth),
      top: clamp(anchorRect.top, offsetHeight, window.innerHeight),
    };
  }

  render() {
    const { job, position } = this.state;
    if (!job) {
      return null;
    }

    return createPortal(
      <div
        ref={this.tooltipRef}
        className="job-summary-tooltip"
        role="tooltip"
        style={position ?? { visibility: 'hidden' }}
      >
        <div className="job-summary-tooltip-title">Job# {job.jobNum}</div>
        {buildJobSummarySections(job).map((section) => (
          <div key={section.title} className="job-summary-tooltip-section">
            <div className="job-summary-tooltip-heading">{section.title}</div>
            {section.rows.map((row) => (
              <div key={row.label} className="job-summary-tooltip-row">
                <span className="job-summary-tooltip-label">{row.label}</span>
                <span
                  className={
                    row.highlight && row.value
                      ? 'job-summary-tooltip-value job-summary-tooltip-highlight'
                      : 'job-summary-tooltip-value'
                  }
                >
                  {row.value || '—'}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>,
      document.body,
    );
  }
}
